/**
 * The idea someone is typing, kept in this browser until it is made into something.
 *
 * An idea box used to live only in its page's memory, so every way off the page lost it:
 * Google One Tap on a landing page (the account is made and the app opens with no idea;
 * of the sign-up sessions that touched a landing page's box in a week, half arrived
 * without it), a link to the plans and back, a back swipe, a reload. One person typed
 * theirs three times before their first video (2026-09-30).
 *
 * One draft per kind of thing ('video', 'scene'), a day long, cleared when it is made
 * and when the account signs out: a shared phone must not hand one person's idea to the next.
 */
import { parseUtc } from './renewal';

const KEY = (kind) => `cs_idea_draft:${kind}`;
const KINDS = ['video', 'scene'];
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const MAX = 4000;

export function saveIdeaDraft(kind, text) {
  try {
    const value = String(text || '');
    if (!value.trim()) window.localStorage.removeItem(KEY(kind));
    else window.localStorage.setItem(KEY(kind), JSON.stringify({ text: value.slice(0, MAX), at: Date.now() }));
  } catch { /* private window, storage full: the box still works, it just forgets */ }
}

export function readIdeaDraft(kind) {
  try {
    const stored = JSON.parse(window.localStorage.getItem(KEY(kind)) || 'null');
    if (!stored || typeof stored.text !== 'string') return '';
    if (!(Date.now() - Number(stored.at) < DRAFT_TTL_MS)) {
      window.localStorage.removeItem(KEY(kind));
      return '';
    }
    return stored.text;
  } catch {
    return '';
  }
}

export function clearIdeaDraft(kind) {
  try { window.localStorage.removeItem(KEY(kind)); } catch { /* nothing to clear */ }
}

export function clearIdeaDrafts() {
  KINDS.forEach(clearIdeaDraft);
}

/**
 * What to open a box with: its own draft, else the one being written in the other box.
 * The first person to make a scene typed it in the video box, read the studio's rail, went
 * to Scenes without pressing Generate, and had to put the same words in again (2026-09-30).
 * Only the guide dialog carried an idea across; now the idea follows whoever changes mode.
 */
export function readIdeaDraftFor(kind) {
  return readIdeaDraft(kind) || KINDS.filter((other) => other !== kind).map(readIdeaDraft).find(Boolean) || '';
}

/** Made: this box's draft goes, and the other box's too when it holds the same words. */
export function clearIdeaDraftMade(kind, text) {
  const made = String(text || '').trim();
  clearIdeaDraft(kind);
  KINDS.filter((other) => other !== kind).forEach((other) => {
    if (made && readIdeaDraft(other).trim() === made) clearIdeaDraft(other);
  });
}

/**
 * Whether a draft typed now will still be here when the plan's videos come back: said at the
 * wall only when it is true (the period ends within the day a draft is kept). People type their
 * next idea with no videos left, three of one morning's eight new accounts.
 */
export function draftOutlasts(periodEnd, now = Date.now()) {
  const end = periodEnd ? parseUtc(periodEnd).getTime() : NaN;
  return Number.isFinite(end) && end > now && end - now < DRAFT_TTL_MS;
}
